import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, Loader2, ArrowLeft } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../stores/authStore';
import { useShiftStore } from '../stores/shiftStore';
import { shiftApi } from '../services/api';
import {
  ShiftType,
  SHIFT_TYPES,
  getAvailableShiftTypes,
  shiftTypeRequiresPreShift,
} from '../config/shiftTypeConfig';

export default function ShiftTypeSelectPage() {
  const navigate = useNavigate();
  const { employee, logout } = useAuthStore();
  const { setShiftType, setStatus, startShift } = useShiftStore();

  const [selectedType, setSelectedType] = useState<ShiftType>('server');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const shiftTypes = getAvailableShiftTypes();

  const handleClockIn = async () => {
    if (!employee) return;

    setLoading(true);
    setError('');

    try {
      const response = await shiftApi.clockIn(employee.id, {
        shiftType: selectedType,
        role: employee.primaryRole,
      });

      if (!response.success) {
        setError(response.error?.message || 'Failed to clock in');
        setLoading(false);
        return;
      }

      // Update shift store
      setShiftType(selectedType);

      // Check if pre-shift is required
      if (shiftTypeRequiresPreShift(selectedType)) {
        setStatus('pre_shift');
        navigate('/pre-shift');
      } else {
        // Skip pre-shift, go directly to active shift
        startShift(response.data.id, null, []);
        navigate('/tables');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    logout();
  };

  const selectedConfig = SHIFT_TYPES[selectedType];

  return (
    // Fixed height (not min-h): the app root is overflow:hidden, so a min-h
    // container just grows and gets clipped — it must own the scrolling.
    <div className="h-[100dvh] bg-gradient-to-br from-primary-900 to-primary-700 flex overflow-y-auto p-4 safe-top safe-bottom scroll-bounce">
      <div className="w-full max-w-md m-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ChefHat className="w-10 h-10 text-white" />
            <img src="/mise-wordmark.svg" alt="mise" className="h-7 invert" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">
            Good {getTimeOfDay()}, {employee?.firstName}!
          </h1>
          <p className="text-primary-200">Select your shift type to clock in</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Switch User</span>
          </button>

          {/* Shift Type Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {shiftTypes.map(({ key, config }) => {
              const Icon = config.icon;
              const isSelected = selectedType === key;

              return (
                <button
                  key={key}
                  onClick={() => setSelectedType(key)}
                  disabled={loading}
                  className={clsx(
                    'flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                    isSelected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                    loading && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div
                    className={clsx(
                      'w-12 h-12 rounded-full flex items-center justify-center mb-2',
                      isSelected ? config.bgColor : 'bg-slate-100'
                    )}
                  >
                    <Icon
                      className={clsx(
                        'w-6 h-6',
                        isSelected ? config.color : 'text-slate-400'
                      )}
                    />
                  </div>
                  <span
                    className={clsx(
                      'font-medium text-sm',
                      isSelected ? 'text-slate-900' : 'text-slate-600'
                    )}
                  >
                    {config.label}
                  </span>
                  <span className="text-xs text-slate-400 mt-0.5">
                    {config.description}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Error Message */}
          {error && (
            <p className="text-center text-danger-500 text-sm mb-4 animate-fade-in">
              {error}
            </p>
          )}

          {/* Clock In Button */}
          <button
            onClick={handleClockIn}
            disabled={loading}
            className={clsx(
              'w-full py-4 rounded-xl font-semibold text-lg transition-all',
              'flex items-center justify-center gap-2',
              'bg-primary-600 text-white hover:bg-primary-700 active:scale-98',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Clocking In...</span>
              </>
            ) : (
              <>
                <selectedConfig.icon className="w-5 h-5" />
                <span>Clock In as {selectedConfig.label}</span>
              </>
            )}
          </button>

          {/* Pre-shift indicator */}
          {shiftTypeRequiresPreShift(selectedType) && (
            <p className="text-center text-slate-500 text-xs mt-3">
              You'll complete a quick pre-shift checklist
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function getTimeOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}
